import { useState, useEffect, useRef, useCallback } from "react";
import { Html5Qrcode } from "html5-qrcode";
import { Button } from "./ui/button";
import { Camera, X, Flashlight, SwitchCamera, Volume2, VolumeX } from "lucide-react";

export function BarcodeScanner({ isOpen, onClose, onScan, language = "ar" }) {
  const [isScanning, setIsScanning] = useState(false);
  const [error, setError] = useState(null);
  const [hasFlash, setHasFlash] = useState(false);
  const [flashOn, setFlashOn] = useState(false);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [cameras, setCameras] = useState([]);
  const [currentCameraIndex, setCurrentCameraIndex] = useState(0);
  const [lastScannedCode, setLastScannedCode] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  
  const scannerRef = useRef(null);
  const html5QrCodeRef = useRef(null);
  const lastScanTimeRef = useRef(0);

  // صوت البيب عند المسح الناجح
  const playBeep = useCallback(() => {
    if (!soundEnabled) return;
    try {
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      
      oscillator.frequency.value = 1200;
      oscillator.type = 'sine';
      gainNode.gain.value = 0.5;
      
      oscillator.start();
      
      setTimeout(() => {
        oscillator.frequency.value = 1500;
      }, 100);
      
      setTimeout(() => {
        oscillator.stop();
        audioContext.close();
      }, 200);
    } catch (e) {
      console.log("Audio not supported");
    }
  }, [soundEnabled]);

  // معالجة الباركود المقروء مع حماية من التكرار
  const handleBarcodeScan = useCallback((decodedText) => {
    const now = Date.now();
    
    // منع القراءة المتكررة خلال 2 ثانية
    if (now - lastScanTimeRef.current < 2000) {
      return;
    }
    
    // منع قراءة نفس الكود مرتين متتاليتين
    if (decodedText === lastScannedCode && now - lastScanTimeRef.current < 5000) {
      return;
    }
    
    // تعيين أننا نقوم بالمعالجة
    if (isProcessing) return;
    setIsProcessing(true);
    
    lastScanTimeRef.current = now;
    setLastScannedCode(decodedText);
    
    // تشغيل صوت البيب
    playBeep();
    
    // إرسال الباركود
    onScan(decodedText);
    
    // إيقاف المسح وإغلاق النافذة
    stopScanner();
    onClose();
    
    // إعادة تعيين حالة المعالجة بعد فترة
    setTimeout(() => {
      setIsProcessing(false);
    }, 1000);
    
  }, [lastScannedCode, isProcessing, playBeep, onScan, onClose]);

  useEffect(() => {
    if (isOpen && !isScanning) {
      // إعادة تعيين الحالة عند الفتح
      setLastScannedCode(null);
      setIsProcessing(false);
      lastScanTimeRef.current = 0;
      startScanner();
    }
    
    return () => {
      stopScanner();
    };
  }, [isOpen]);

  const startScanner = async () => {
    try {
      setError(null);
      
      const devices = await Html5Qrcode.getCameras();
      if (devices && devices.length > 0) {
        setCameras(devices);
        
        // تفضيل الكاميرا الخلفية
        const backCameraIndex = devices.findIndex(d => 
          d.label.toLowerCase().includes('back') || 
          d.label.toLowerCase().includes('rear') ||
          d.label.toLowerCase().includes('environment')
        );
        const cameraIndex = backCameraIndex !== -1 ? backCameraIndex : 0;
        setCurrentCameraIndex(cameraIndex);
        
        await initScanner(devices[cameraIndex].id);
      } else {
        setError(language === "ar" ? "لم يتم العثور على كاميرا" : "No camera found");
      }
    } catch (err) {
      console.error("Camera error:", err);
      setError(language === "ar" 
        ? "فشل الوصول للكاميرا. تأكد من إعطاء الإذن" 
        : "Failed to access camera. Please grant permission");
    }
  };

  const initScanner = async (cameraId) => {
    try {
      if (html5QrCodeRef.current) {
        try {
          await html5QrCodeRef.current.stop();
        } catch (e) {
          // تجاهل الخطأ إذا كان الماسح متوقف بالفعل
        }
        html5QrCodeRef.current = null;
      }
      
      html5QrCodeRef.current = new Html5Qrcode("barcode-scanner-region");
      
      await html5QrCodeRef.current.start(
        cameraId,
        {
          fps: 5, // تقليل FPS لتجنب القراءة المتكررة
          qrbox: { width: 280, height: 150 },
          aspectRatio: 1.777,
        },
        (decodedText, decodedResult) => {
          handleBarcodeScan(decodedText);
        },
        (errorMessage) => {
          // تجاهل أخطاء المسح المستمرة
        }
      );
      
      setIsScanning(true);
      
      // التحقق من دعم الفلاش
      try {
        const track = html5QrCodeRef.current.getRunningTrackCameraCapabilities?.();
        if (track?.torchFeature?.isSupported()) {
          setHasFlash(true);
        }
      } catch (e) {
        setHasFlash(false);
      }
      
    } catch (err) {
      console.error("Scanner init error:", err);
      setError(language === "ar" ? "فشل تشغيل الماسح" : "Failed to start scanner");
    }
  };

  const stopScanner = async () => {
    try {
      if (html5QrCodeRef.current) {
        const state = html5QrCodeRef.current.getState?.();
        if (state === 2) { // SCANNING state
          await html5QrCodeRef.current.stop();
        }
        html5QrCodeRef.current = null;
      }
    } catch (err) {
      console.error("Stop scanner error:", err);
    }
    setIsScanning(false);
  };

  const toggleFlash = async () => {
    try {
      const track = html5QrCodeRef.current?.getRunningTrackCameraCapabilities?.();
      if (track?.torchFeature) {
        await track.torchFeature.apply(!flashOn);
        setFlashOn(!flashOn);
      }
    } catch (e) {
      console.log("Flash toggle failed");
    }
  };

  const switchCamera = async () => {
    if (cameras.length <= 1) return;
    
    const newIndex = (currentCameraIndex + 1) % cameras.length;
    setCurrentCameraIndex(newIndex);
    await stopScanner();
    await initScanner(cameras[newIndex].id);
  };

  const handleClose = () => {
    stopScanner();
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] bg-black">
      {/* Header */}
      <div className="absolute top-0 left-0 right-0 z-10 bg-gradient-to-b from-black/80 to-transparent p-4">
        <div className="flex items-center justify-between">
          <Button
            variant="ghost"
            size="icon"
            onClick={handleClose}
            className="text-white hover:bg-white/20"
          >
            <X className="w-6 h-6" />
          </Button>
          
          <h2 className="text-white font-bold text-lg">
            {language === "ar" ? "مسح الباركود" : "Scan Barcode"}
          </h2>
          
          <div className="flex gap-2">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setSoundEnabled(!soundEnabled)}
              className="text-white hover:bg-white/20"
            >
              {soundEnabled ? <Volume2 className="w-5 h-5" /> : <VolumeX className="w-5 h-5" />}
            </Button>
            
            {cameras.length > 1 && (
              <Button
                variant="ghost"
                size="icon"
                onClick={switchCamera}
                className="text-white hover:bg-white/20"
              >
                <SwitchCamera className="w-5 h-5" />
              </Button>
            )}
            
            {hasFlash && (
              <Button
                variant="ghost"
                size="icon"
                onClick={toggleFlash}
                className={`text-white hover:bg-white/20 ${flashOn ? 'bg-yellow-500/30' : ''}`}
              >
                <Flashlight className="w-5 h-5" />
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Scanner Region */}
      <div className="h-full flex items-center justify-center">
        <div className="relative w-full max-w-lg">
          <div 
            id="barcode-scanner-region" 
            ref={scannerRef}
            className="w-full"
            style={{ minHeight: '300px' }}
          />
          
          {/* إطار المسح */}
          <div className="absolute inset-0 pointer-events-none flex items-center justify-center">
            <div className="relative w-72 h-40">
              <div className="absolute top-0 left-0 w-8 h-8 border-t-4 border-l-4 border-emerald-500 rounded-tl-lg" />
              <div className="absolute top-0 right-0 w-8 h-8 border-t-4 border-r-4 border-emerald-500 rounded-tr-lg" />
              <div className="absolute bottom-0 left-0 w-8 h-8 border-b-4 border-l-4 border-emerald-500 rounded-bl-lg" />
              <div className="absolute bottom-0 right-0 w-8 h-8 border-b-4 border-r-4 border-emerald-500 rounded-br-lg" />
              
              <div className="absolute inset-x-2 h-0.5 bg-emerald-500 animate-scan-line" 
                   style={{ animation: 'scanLine 2s ease-in-out infinite' }} 
              />
            </div>
          </div>
          
          {/* مؤشر المعالجة */}
          {isProcessing && (
            <div className="absolute inset-0 flex items-center justify-center bg-black/50">
              <div className="bg-emerald-500 text-white px-6 py-3 rounded-xl font-bold animate-pulse">
                {language === "ar" ? "جاري المعالجة..." : "Processing..."}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Footer */}
      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-6">
        {error ? (
          <div className="text-center">
            <p className="text-red-400 mb-4">{error}</p>
            <Button onClick={startScanner} className="bg-emerald-600 hover:bg-emerald-700">
              {language === "ar" ? "إعادة المحاولة" : "Retry"}
            </Button>
          </div>
        ) : (
          <div className="text-center">
            <div className="flex items-center justify-center gap-2 text-emerald-400 mb-2">
              <Camera className="w-5 h-5" />
              <span className="font-medium">
                {language === "ar" ? "وجّه الكاميرا نحو الباركود" : "Point camera at barcode"}
              </span>
            </div>
            <p className="text-slate-400 text-sm">
              {language === "ar" 
                ? "سيتم إضافة المنتج تلقائياً للسلة" 
                : "Product will be added to cart automatically"}
            </p>
          </div>
        )}
      </div>

      <style>{`
        @keyframes scanLine {
          0%, 100% { top: 10%; }
          50% { top: 85%; }
        }
        #barcode-scanner-region video {
          border-radius: 12px;
        }
        #barcode-scanner-region {
          border: none !important;
        }
        #qr-shaded-region {
          border: none !important;
        }
      `}</style>
    </div>
  );
}
